<script setup lang="ts">
import 'roboto-fontface/css/roboto/roboto-fontface.css';
import Navbar from '@/components/layout/NavBar.vue';
import Loading from '@/components/LoadingScreen.vue';
import Popup from '@/components/PopUp.vue';
import ToastNotification from '@/components/ui/ToastNotification.vue';
import { useI18n } from 'vue-i18n';
import { AppRoute } from './libs/routing';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Common from './libs/common';
import { useAppStore, type AppLoadingState } from './stores/app';
import { useGroupStore } from './stores/group';
import { useGtfsStore } from './stores/gtfs';
import { useAlertsStore } from './stores/alerts';
import { useFavoriteNotificationsStore } from './stores/favorite-notifications';
import type { Group } from './@types/group';
import { initIntercomForCapacitor } from '@/libs/intercom';
import { isKnownGroup, openPushNotification } from '@/libs/push-navigation';
import api from '@/api';
import { isAppVisible } from '@/libs/app-visibility';

const appStore = useAppStore();
const groupStore = useGroupStore();
const gtfsStore = useGtfsStore();
const alertsStore = useAlertsStore();
const favoriteNotificationsStore = useFavoriteNotificationsStore();

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// The settings and search pages are stacked pages: they cover the tab bar.
const NAVBAR_LESS_ROUTES: string[] = [
  AppRoute.SELECT_GROUP,
  AppRoute.SETTINGS,
  AppRoute.SETTINGS_LANGUAGES,
  AppRoute.SETTINGS_CONTACT,
  AppRoute.SEARCH,
];

const displayNavbar = computed<boolean>(() => {
  return (
    !loading.value &&
    groupId.value !== undefined &&
    !NAVBAR_LESS_ROUTES.includes(route.name?.toString() ?? '')
  );
});

const groupId = computed<string | undefined>(() => {
  return (route.params.groupId as string) || groupStore.group?.group_id;
});

const groupName = computed<string | undefined>(() => {
  return groupStore.group?.name;
});

const isGtfsLoadingOnError = computed<boolean>(() => {
  return gtfsStore.isGtfsLoadingOnError;
});

const loading = computed<boolean | AppLoadingState>(() => {
  return appStore.isLoading;
});

const transitionStyle = computed<string>(() => {
  const previousPage = appStore.previousPage;
  switch (route.name) {
    case AppRoute.ROUTE_DETAILED:
      return previousPage === AppRoute.ROUTE_LIST ? 'slide-left' : 'fade';
    case AppRoute.ROUTE_LIST:
      return previousPage === AppRoute.ROUTE_DETAILED ? 'slide-right' : 'fade';
    case AppRoute.STOP_DETAILED:
      switch (previousPage) {
        case AppRoute.STOP_LIST:
          return 'slide-left';
        case AppRoute.TIMETABLE:
        case AppRoute.TRIP_DETAILED:
          return 'slide-right';
        default:
          return 'fade';
      }
    case AppRoute.STOP_LIST:
      return previousPage === AppRoute.STOP_DETAILED ? 'slide-right' : 'fade';
    case AppRoute.TIMETABLE:
      return previousPage === AppRoute.STOP_DETAILED ? 'slide-left' : 'fade';
    default:
      return 'fade';
  }
});

watch(
  route,
  function (_, previousRoute) {
    appStore.previousPage = previousRoute?.name as string;
  },
  { immediate: true },
);

watch(groupId, groupIdValue => {
  if (groupIdValue === groupStore.group?.group_id) return;
  if (groupIdValue === groupStore.requestedGroupId) return;

  if (groupIdValue) {
    groupStore.connectGroup(groupIdValue);
    initIntercomForCapacitor(groupId.value);
  }
});

watch(
  groupName,
  () => {
    if (groupName.value) document.title = groupName.value + ' - Pysae';
    else document.title = 'Pysae';
  },
  { immediate: true },
);

watch(isGtfsLoadingOnError, () => {
  if (isGtfsLoadingOnError.value) {
    setTimeout(() => {
      appStore.showPopup({
        text: t('error.loadingGtfs'),
        icon: 'globe-off',
        reloadGtfsButton: true,
      });
    }, 5000);
  }
});

const DAILY_REFRESH_INTERVAL = 300000;
let dailyRefreshInterval: number | undefined;

function refreshTodayAndAlerts() {
  appStore.updateToday();
  alertsStore.getAlerts();
}

function startDailyRefresh() {
  dailyRefreshInterval = window.setInterval(refreshTodayAndAlerts, DAILY_REFRESH_INTERVAL);
}

watch(isAppVisible, visible => {
  window.clearInterval(dailyRefreshInterval);
  dailyRefreshInterval = undefined;
  if (!visible) return;
  if (appStore.isNativePlateform) favoriteNotificationsStore.refreshPermission();
  refreshTodayAndAlerts();
  startDailyRefresh();
});

onMounted(async () => {
  const groupFromLs: Group | null = Common.safeLSParse('group');
  if (groupFromLs) groupStore.connectGroup(groupFromLs.group_id);

  if (isAppVisible.value) startDailyRefresh();

  if (appStore.isNativePlateform) {
    // Android hardware back button: registered handlers (e.g. a full-screen
    // bottom sheet collapsing itself) take precedence over history navigation.
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (appStore.handleBackButton()) return;
      if (canGoBack) window.history.back();
      else CapacitorApp.minimizeApp();
    });

    // Add native-app class for edge-to-edge safe-area CSS handling on all native platforms
    document.documentElement.classList.add('native-app');
    if (Capacitor.getPlatform() === 'ios') {
      document.documentElement.classList.add('ios-native');
    }

    // Before the push prompt: two system dialogs cannot be open at once.
    await appStore.requestGeolocationAtLaunch();

    // Push setup must never surface as an uncaught rejection: a denied
    // permission or a transient FCM failure (SERVICE_NOT_AVAILABLE) is logged,
    // not thrown.
    try {
      // Ask permission (iOS only)
      await PushNotifications.requestPermissions();
      await PushNotifications.register();

      await PushNotifications.addListener('registration', token => {
        console.info('Push registration success, FCM token:', token.value);
      });

      await PushNotifications.addListener('registrationError', err => {
        console.error('Push registration failed:', err.error);
      });

      await PushNotifications.addListener('pushNotificationReceived', notification => {
        console.info('Push received in foreground:', notification);
      });

      // When notification is clicked and app is in background or closed
      await PushNotifications.addListener('pushNotificationActionPerformed', action => {
        openPushNotification(action.notification.data, {
          currentGroupId: groupId.value,
          isKnownGroup: id => isKnownGroup(id, api.getGroup),
          push: router.push,
        });
      });

      // The permission prompt was open until now: read the answer and subscribe
      // the favorites in this session rather than at the next launch.
      await favoriteNotificationsStore.refreshPermission();
      await favoriteNotificationsStore.sync();
    } catch (error) {
      console.error('Push registration failed:', error);
    }
  }
});
</script>
<template>
  <div class="app">
    <router-view v-slot="{ Component }">
      <transition :name="transitionStyle">
        <keep-alive :include="['MapIndex', 'SchedulesPage']" :max="2">
          <component :is="Component" />
        </keep-alive>
      </transition>
    </router-view>

    <transition name="fade">
      <Loading v-if="loading" />
    </transition>
    <Popup />
    <ToastNotification />

    <Navbar v-if="displayNavbar && groupId" :group-id="groupId" />
  </div>
</template>

<style lang="scss" src="@/assets/css/global.scss"></style>
